import { Box, Button, Card, Grid, styled, Typography } from "@mui/material";
import contactAPI, { Contact } from "apis/ContactAPI";
import PageToolbar from "components/navigation/PageToolbar";
import useTitle from "hooks/useTitle";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: 16,
  backgroundColor: theme.palette.background.paper,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  marginBottom: theme.spacing(0.75),
  color: theme.palette.text.secondary,
}));

const SectionContent = styled(Typography)(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "#f7f9fc",
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  borderRadius: 12,
  minHeight: 56,
  display: "flex",
  alignItems: "center",
  color: theme.palette.text.primary,
}));

const ContactView: FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [title, setTitle] = useState<string>("Contato");
  useTitle(title);

  const [contactForm, setContactForm] = useState<Contact>();
  const { id } = useParams();

  useEffect(() => {
    if (id === undefined) return;

    contactAPI.getById(id).then((response) => {
      const result = response.data as Contact;
      setTitle(result.name);
      setContactForm(result);
    });
  }, [id]);

  const handleEdit = () => {
    if (!contactForm?.id) return;
    navigate(`/main/contact/add-edit/${contactForm.id}`);
  };

  const handleDelete = () => {
    if (!contactForm?.id) return;

    contactAPI.remove(contactForm.id).then((response) => {
      if (response.status === 204) {
        toast.success("Contato removido com sucesso.");
        navigate(`/main/contact`);
      }
    });
  };

  const getMainAddress = () => {
    if (!contactForm?.addresses?.[0]) return "Não informado";

    const address = contactForm.addresses[0];
    return `${address.street || ""}, ${address.number || ""} - ${
      address.district || ""
    } - ${address.city || ""} - ${address.state || ""} - ${
      address.zipCode || ""
    } - Brasil`;
  };

  const getMainEmail = () => {
    return contactForm?.emails?.[0]?.email || "Não informado";
  };

  const getMainPhone = () => {
    return contactForm?.phones?.[0]?.telephone || "Não informado";
  };

  return (
    <Box pt={2} pb={4}>
      <StyledCard>
        <PageToolbar
          onBack={() => navigate(-1)}
          title={contactForm?.name || t("Detalhes do contato")}
          actions={
            <>
              <Button variant="outlined" onClick={handleEdit}>
                {t("Editar")}
              </Button>
              <Button variant="contained" color="error" onClick={handleDelete}>
                {t("Remover")}
              </Button>
            </>
          }
        />

        <Grid container spacing={2}>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("Nome")}</SectionTitle>
            <SectionContent variant="body1">
              {contactForm?.name || "Não informado"}
            </SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("Apelido")}</SectionTitle>
            <SectionContent variant="body1">
              {contactForm?.nickname || "Não informado"}
            </SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("CPF")}</SectionTitle>
            <SectionContent variant="body1">
              {contactForm?.taxDocumentNumber || "Não informado"}
            </SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("Endereço")}</SectionTitle>
            <SectionContent variant="body1">{getMainAddress()}</SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">
              {t("Data de Nascimento")}
            </SectionTitle>
            <SectionContent variant="body1">
              {contactForm?.birthDay || "Não informado"}
            </SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("Profissão")}</SectionTitle>
            <SectionContent variant="body1">
              {contactForm?.job || "Não informado"}
            </SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("Email")}</SectionTitle>
            <SectionContent variant="body1">{getMainEmail()}</SectionContent>
          </Grid>
          <Grid item sm={6} xs={12}>
            <SectionTitle variant="subtitle2">{t("Telefone")}</SectionTitle>
            <SectionContent variant="body1">{getMainPhone()}</SectionContent>
          </Grid>
        </Grid>
      </StyledCard>
    </Box>
  );
};

export default ContactView;
